import { Component } from '@theme/component';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

/**
 * @typedef {Object} MobileShopMenuRefs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/** @extends {Component<MobileShopMenuRefs>} */
class MobileShopMenu extends Component {
  requiredRefs = ['tabs', 'panels'];

  connectedCallback() {
    super.connectedCallback();

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.#selectTab(index));
    });
  }

  /**
   * @param {number} index
   */
  selectTab(index) {
    this.#selectTab(index);
  }

  /**
   * @param {number} index
   */
  #selectTab(index) {
    const { tabs, panels } = this.refs;

    tabs.forEach((tab, tabIndex) => {
      const isSelected = tabIndex === index;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.tabIndex = isSelected ? 0 : -1;
    });

    panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index;
    });
  }
}

/** @extends {DialogComponent} */
class MobileShopMenuDrawer extends DialogComponent {
  /** @type {HTMLButtonElement | null} */
  #trigger = null;

  connectedCallback() {
    super.connectedCallback();
    this.#trigger = document.querySelector('.mobile-shop-menu__trigger');
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
  }

  showDialog() {
    this.#updateDialogOffset();
    super.showDialog();
  }

  /**
   * Opens the shop menu from the main menu drawer.
   * @param {number | Event} [tabIndex]
   * @param {Event} [event]
   */
  openFromMainMenu = (tabIndex, event) => {
    if (tabIndex instanceof Event) {
      event = tabIndex;
      tabIndex = 0;
    }

    event?.preventDefault();

    const shopMenu = this.querySelector('mobile-shop-menu');
    if (shopMenu instanceof MobileShopMenu) {
      shopMenu.selectTab(Number(tabIndex) || 0);
    }

    const headerDrawer = document.querySelector('header-drawer');

    if (headerDrawer?.isOpen && typeof headerDrawer.close === 'function') {
      const details = headerDrawer.refs?.details;

      if (details instanceof HTMLDetailsElement) {
        details.addEventListener(
          'toggle',
          () => {
            if (!details.open) {
              this.showDialog();
            }
          },
          { once: true }
        );
      }

      headerDrawer.close();
      return;
    }

    this.showDialog();
  };

  #updateDialogOffset() {
    const headerGroup = document.querySelector('#header-group');
    const header = document.querySelector('#header-component');
    const anchor = headerGroup ?? header;

    if (!(anchor instanceof HTMLElement)) return;

    const offset = Math.round(anchor.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--mobile-shop-menu-offset', `${offset}px`);
  }

  #handleOpen = () => {
    this.#updateTriggerState(true);
  };

  #handleClose = () => {
    this.#updateTriggerState(false);
  };

  /**
   * @param {boolean} isOpen
   */
  #updateTriggerState(isOpen) {
    if (!this.#trigger) return;

    this.#trigger.classList.toggle('is-open', isOpen);
    this.#trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }
}

if (!customElements.get('mobile-shop-menu')) {
  customElements.define('mobile-shop-menu', MobileShopMenu);
}

if (!customElements.get('mobile-shop-menu-drawer')) {
  customElements.define('mobile-shop-menu-drawer', MobileShopMenuDrawer);
}
